import { useId } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Printer, Signpost } from 'lucide-react';
import { portalQueries } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { formatDate, formatMoney, hasPermission } from '@blimo/shared';
import { Badge, Button, EmptyState, ErrorState, Skeleton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, cn } from '@blimo/ui';
import { unitTitle } from '../lib/units.js';
import { PageHeader } from '../components/ui/page-header.jsx';
import { Section } from '../components/ui/section.jsx';
import { Switch } from '../components/ui/switch.jsx';
import { ForbiddenPage } from './pages.jsx';
import { VatToggle } from './finance.jsx';

const AMOUNTS = [
  { key: 'base_rent', label: 'Miete' },
  { key: 'parking_rent', label: 'Stellplatz' },
  { key: 'operating_costs', label: 'Betriebskosten' },
  { key: 'heating_costs', label: 'Heizkosten' },
];

/**
 * Vermietung (F-608, G-21): eigene Einheiten mit aktuellem Mietverhältnis, Sollmiete laut WinCasa (netto/brutto) und
 * Stand des Vermietungsvorgangs. Alle Beträge, Summen und die Netto-Umrechnung kommen aus der API; keine Mieternamen.
 */
export function LettingsPage() {
  const api = useApi();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const openId = useId();
  const canView = hasPermission(me, 'lettings.view');
  const open = search.open === 1;
  const vat = search.vat === 'gross' ? 'gross' : 'net';
  const params = { ...(open ? { open: true } : {}), ...(vat === 'gross' ? { vat } : {}) };
  const query = useQuery({ ...portalQueries.lettingOverview(api, params), enabled: canView });
  if (!canView) return <ForbiddenPage />;

  const setSearch = (patch) =>
    navigate({
      to: '/vermietung',
      search: (prev) => Object.fromEntries(Object.entries({ ...prev, ...patch }).filter(([, v]) => v !== undefined)),
      replace: true,
    });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Vermietung"
        description="Ihre Einheiten mit aktuellem Mietverhältnis, Sollmiete laut Verwaltung und Stand der Vermietungsvorgänge."
        actions={
          <Button type="button" variant="outline" className="print:hidden" onClick={() => window.print()}>
            <Printer aria-hidden="true" /> Drucken
          </Button>
        }
      />
      <div className="flex flex-col gap-3 rounded-xl border bg-card p-3 sm:flex-row sm:flex-wrap sm:items-end sm:px-4 print:hidden">
        <VatToggle value={search.vat} fallback="net" onChange={(v) => setSearch({ vat: v })} />
        <div className="flex items-center gap-2.5 sm:pb-2">
          <Switch id={openId} checked={open} onCheckedChange={(checked) => setSearch({ open: checked ? 1 : undefined })} />
          <label htmlFor={openId} className="text-sm font-medium">
            Nur Einheiten mit offenem Vermietungsvorgang
          </label>
        </div>
      </div>
      {query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : query.isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : query.data.properties.length === 0 ? (
        <EmptyState
          icon={Signpost}
          title={open ? 'Keine offenen Vermietungsvorgänge' : 'Keine Einheiten'}
          description={open ? 'Für Ihre Einheiten liegen derzeit keine offenen Vermietungsvorgänge vor.' : 'Ihnen sind derzeit keine Einheiten zugeordnet.'}
        />
      ) : (
        <div className={cn('flex flex-col gap-5', query.isPlaceholderData && 'opacity-60')}>
          <p className="text-sm text-muted-foreground" data-testid="vat-basis">
            {query.data.vat_basis === 'gross'
              ? 'Bruttobeträge wie vereinbart (bei Mietverhältnissen mit MwSt-Ausweis inkl. MwSt).'
              : `Nettobeträge – bei Mietverhältnissen mit MwSt-Ausweis ohne ${query.data.vat_rate.toLocaleString('de-DE')} % MwSt.`}
          </p>
          {query.data.properties.map((group) => (
            <PropertyGroup key={group.property?.id} group={group} />
          ))}
        </div>
      )}
    </div>
  );
}

function TenancyCell({ row }) {
  if (row.status === 'vacant') return <Badge variant="neutral">Leer</Badge>;
  const t = row.tenancy;
  return (
    <span className="flex flex-col gap-0.5">
      <span className="flex flex-wrap items-center gap-1">
        <Badge variant="success">Vermietet</Badge>
        {t?.vat_reporting && <Badge variant="neutral">MwSt-Ausweis</Badge>}
      </span>
      <span className="text-xs text-muted-foreground">
        {t?.starts_on ? `seit ${formatDate(t.starts_on)}` : null}
        {t?.terminated_on ? ` · gekündigt ${formatDate(t.terminated_on)}` : null}
        {t?.ends_on ? ` · endet ${formatDate(t.ends_on)}` : null}
      </span>
    </span>
  );
}

function TicketCell({ ticket }) {
  if (!ticket) return <span className="text-muted-foreground">–</span>;
  return (
    <span className="flex flex-col gap-0.5">
      <span className="flex flex-wrap items-center gap-1">
        <Badge variant={ticket.is_open ? 'warning' : 'success'}>{ticket.is_open ? 'Offen' : 'Erledigt'}</Badge>
        {ticket.status && <span className="text-xs text-muted-foreground">{ticket.status}</span>}
      </span>
      {ticket.title && <span className="text-xs">{ticket.title}</span>}
      {ticket.termination_confirmed_on && (
        <span className="text-xs text-muted-foreground">Kündigung bestätigt {formatDate(ticket.termination_confirmed_on)}</span>
      )}
    </span>
  );
}

function PropertyGroup({ group }) {
  const name = group.property?.name || `Liegenschaft #${group.property?.id}`;
  const { totals } = group;
  return (
    <Section
      title={name}
      description={`${totals.units} ${totals.units === 1 ? 'Einheit' : 'Einheiten'} · ${totals.let} vermietet · ${totals.vacant} leer${totals.open_tickets ? ` · ${totals.open_tickets} ${totals.open_tickets === 1 ? 'offener Vorgang' : 'offene Vorgänge'}` : ''}`}
      flush
      className="print:break-inside-avoid"
    >
      <Table aria-label={`Vermietung ${name}`} containerClassName="print:overflow-visible">
        <TableHeader className="print:static">
          <TableRow>
            <TableHead>Einheit</TableHead>
            <TableHead>Mietverhältnis</TableHead>
            {AMOUNTS.map((a) => (
              <TableHead key={a.key} data-numeric="true">
                {a.label}
              </TableHead>
            ))}
            <TableHead data-numeric="true">Gesamt</TableHead>
            <TableHead>Vermietungsvorgang</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {group.units.map((row) => (
            <TableRow key={row.unit.id}>
              <TableCell className="font-medium">{unitTitle(row.unit)}</TableCell>
              <TableCell>
                <TenancyCell row={row} />
              </TableCell>
              {AMOUNTS.map((a) => (
                <TableCell key={a.key} data-numeric="true" className="numeric text-right">
                  {row.rent ? formatMoney(row.rent[a.key]) : '–'}
                </TableCell>
              ))}
              <TableCell data-numeric="true" className="numeric text-right font-medium">
                {row.rent ? formatMoney(row.rent.total) : '–'}
              </TableCell>
              <TableCell>
                <TicketCell ticket={row.ticket} />
              </TableCell>
            </TableRow>
          ))}
          <TableRow className="bg-muted/50 font-semibold" aria-label={`Summe ${name}`}>
            <TableCell colSpan={2}>Summe</TableCell>
            {AMOUNTS.map((a) => (
              <TableCell key={a.key} data-numeric="true" className="numeric text-right">
                {formatMoney(totals[a.key])}
              </TableCell>
            ))}
            <TableCell data-numeric="true" className="numeric text-right">
              {formatMoney(totals.total)}
            </TableCell>
            <TableCell />
          </TableRow>
        </TableBody>
      </Table>
    </Section>
  );
}
